import { FC, KeyboardEvent, memo } from 'react';
import { Tooltip } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { ClassroomMeetingType } from '../../utils/interface';
import { formatMeetingDate, formatMeetingTime } from '../../utils/attendanceDateRange';
import './AttendanceCard.scss';

interface AttendanceCardProps {
  meeting: ClassroomMeetingType;
  /** Opens the meeting's attendance report */
  onOpen?: (meeting: ClassroomMeetingType) => void;
}

const AttendanceCard: FC<AttendanceCardProps> = ({ meeting, onOpen }) => {
  const { topic, startTime, endTime } = meeting;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen?.(meeting);
    }
  };

  return (
    <div
      className={`attendanceCard${onOpen ? ' attendanceCard--clickable' : ''}`}
      {...(onOpen && {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Attendance report for ${topic}`,
        onClick: () => onOpen(meeting),
        onKeyDown: handleKeyDown,
      })}
    >
      <Tooltip title={topic ?? ''} placement='bottom-start' disableInteractive>
        <div className='attendanceCardTopic'>{topic}</div>
      </Tooltip>
      <div className='attendanceCardMeta'>
        <div className='attendanceCardMetaRow'>
          <CalendarTodayOutlinedIcon className='attendanceCardMetaIcon' />
          <span className='attendanceCardDate'>{formatMeetingDate(startTime)}</span>
        </div>
        <div className='attendanceCardMetaRow'>
          <AccessTimeOutlinedIcon className='attendanceCardMetaIcon' />
          <span className='attendanceCardTime'>{formatMeetingTime(startTime, endTime)}</span>
        </div>
      </div>
    </div>
  );
};

export default memo(AttendanceCard);
